import React from "react";
import { useSelector } from "react-redux";
import styles from "../widget-renderer.module.scss";
import type { WidgetRendererProps } from "../widget-renderer";
import { selectUnitsById } from "@src/reducers/units-slice";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { CounterNumberGauge } from "./counter-number-gauge";
import { getPlaceholderValue } from "../get-placeholder-value";
import { useLastWidgetValue } from "../use-last-widget-value";
import { applyFieldTransforms } from "../utils/value-scaling";
import { DURATION_GAUGE_WIDGET_MS, isoToDurationMs } from "../constants";
import { NoDataInTimeframe } from "@src/components/atoms/no-data-in-timeframe/no-data-in-timeframe";
import Spinner from "@src/components/atoms/spinner/spinner";

/**
 * Gauge bound to a live scalar query. Owns its data fetch so the renderer can
 * stay a generic type → component dispatcher (line chart etc. will own their
 * own hooks). Preview passes an explicit `value`; live widgets fetch it.
 */
export const CounterNumberWidget = ({
  title,
  semantics,
  presentation,
  encoding,
  query,
  value,
  preview = false,
  showTitle = true,
  // Off-screen tile: ticks skip fetching; the interval keeps its schedule;
  // re-entry refetches once via the visibility bridge.
  inViewport = true,
}: WidgetRendererProps) => {
  const units = useSelector(selectUnitsById);
  const unit = getUnitById(units, presentation?.unit);

  // Preview never fetches; live widgets fetch from the configured data source.
  // Lookback persisted as ISO in `query.window.duration` (mirrors the line
  // chart's duration round-trip); null/undefined → legacy 30 min default.
  const live = useLastWidgetValue(
    preview ? undefined : query,
    isoToDurationMs(query?.window?.duration) ?? DURATION_GAUGE_WIDGET_MS,
    inViewport,
  );
  const resolvedValue = value ?? live.value ?? getPlaceholderValue(semantics);
  // The hook now accepts string-valued nodes (single-text-value widens the
  // scope), but the gauge dial only renders numbers. Strings collapse to the
  // placeholder — a numeric gauge node producing a string is a server-side
  // anomaly, not a drawable input. Scale applies to the numeric value before
  // it reaches the dial; strings pass through unscaled.
  const scaledValue = applyFieldTransforms(
    "value",
    resolvedValue,
    encoding?.transforms,
  );
  const numericValue =
    typeof scaledValue === "number"
      ? scaledValue
      : getPlaceholderValue(semantics);
  // Node-driven bounds arrive in the node's raw units, so they take the same
  // factor as the value. A null bound means the drawer's static number is used
  // instead — that one is typed in displayed units and is never scaled.
  const scaleBound = (bound: number | null) => {
    if (bound == null) return null;
    const scaled = applyFieldTransforms("value", bound, encoding?.transforms);
    return typeof scaled === "number" ? scaled : null;
  };
  const isLoading = preview ? false : !!query && live.isLoading;
  // Explicit preview `value` always wins; empty/error states only apply to
  // live data — both render the same "no data" message.
  const isEmpty =
    !preview &&
    value === undefined &&
    !!query &&
    (live.hasNoData || !!live.error);

  return (
    <div className={styles["widget-renderer__body"]}>
      {isLoading ? (
        <div className={styles["widget-renderer__loading"]}>
          <Spinner />
        </div>
      ) : isEmpty ? (
        <NoDataInTimeframe />
      ) : (
        <CounterNumberGauge
          title={title}
          semantics={semantics}
          presentation={presentation}
          unit={unit}
          value={numericValue}
          boundValues={{
            min: scaleBound(live.boundValues.min),
            max: scaleBound(live.boundValues.max),
            target: scaleBound(live.boundValues.target),
          }}
          lastTimestamp={live.lastTimestamp}
          showTitle={showTitle}
        />
      )}
    </div>
  );
};
